import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { usePrivy } from '@privy-io/react-auth'
import { signInWithCustomToken, signOut as signOutFirebase } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import type { AppUser, AuthContextType } from '@/types/auth'

const AuthContext = createContext<AuthContextType | undefined>(undefined)
const firebaseAuthBridgeEnabled = import.meta.env.VITE_ENABLE_FIREBASE_AUTH_BRIDGE === 'true'
const isDev = import.meta.env.DEV

function mapPrivyUser(privyUser: ReturnType<typeof usePrivy>['user']): AppUser | null {
  if (!privyUser) return null

  const email = privyUser.email?.address ?? null
  const wallet = privyUser.wallet?.address ?? null
  const google = privyUser.google

  return {
    uid: privyUser.id,
    email,
    displayName: google?.name ?? email?.split('@')[0] ?? wallet?.slice(0, 8) ?? null,
    photoURL: (google as { picture?: string } | undefined)?.picture ?? null,
    emailVerified: !!privyUser.email?.address,
    plan: 'free',
    reload: async () => { /* no-op for Privy */ }
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { ready, authenticated, user: privyUser, login: privyLogin, logout: privyLogout, getAccessToken } = usePrivy()
  const [appUser, setAppUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!ready) return

    let cancelled = false

    const syncAuthState = async () => {
      setLoading(true)

      if (authenticated && privyUser) {
        const mapped = mapPrivyUser(privyUser)

        if (mapped && firebaseAuthBridgeEnabled && auth && auth.currentUser?.uid !== mapped.uid) {
          try {
            const accessToken = await getAccessToken()

            if (accessToken) {
              const response = await fetch('/api/auth/firebase-token', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                },
              })

              if (!response.ok) {
                throw new Error(`Bridge request failed with status ${response.status}`)
              }

              const data = await response.json()

              if (!data?.firebaseToken) {
                throw new Error('Bridge response did not include a Firebase token')
              }

              await signInWithCustomToken(auth, data.firebaseToken)
            }
          } catch (error) {
            if (isDev) {
              console.error('Firebase auth bridge failed:', error)
            }
          }
        }

        if (!cancelled) {
          setAppUser(mapped)
        }
      } else {
        if (auth?.currentUser) {
          try {
            await signOutFirebase(auth)
          } catch (error) {
            if (isDev) {
              console.error('Firebase sign-out failed:', error)
            }
          }
        }

        if (!cancelled) {
          setAppUser(null)
        }
      }

      if (!cancelled) {
        setLoading(false)
      }
    }

    void syncAuthState()

    return () => {
      cancelled = true
    }
  }, [ready, authenticated, privyUser, getAccessToken])

  const login = useCallback(() => {
    privyLogin()
  }, [privyLogin])

  const logout = useCallback(async () => {
    await privyLogout()
    if (auth?.currentUser) {
      try {
        await signOutFirebase(auth)
      } catch (error) {
        if (isDev) {
          console.error('Firebase sign-out failed:', error)
        }
      }
    }
    setAppUser(null)
  }, [privyLogout])

  const value: AuthContextType = {
    user: appUser,
    loading,
    login,
    logout,
    signInWithEmail: async () => { privyLogin() },
    signInWithGoogle: async () => { privyLogin() },
    signUpWithGoogle: async () => { privyLogin() },
    signOut: logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
